<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="workOrdersFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            :height="$vuetify.breakpoint.mdAndDown ? '65vh' : '65vh'"
            :footer-props="{
                itemsPerPageOptions: [15, 30, 100, -1],
                itemsPerPageText: 'Work Orders Per Page',
            }"
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex align-center mb-n3">
                        <h1 class="mr-4">WORK ORDERS</h1>
                        <v-btn
                            color="primary"
                            @click="exportApprovedWOReport"
                            class="ml-auto"
                        >
                            <v-icon left small>mdi-file-excel</v-icon>
                            Report
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.code`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="search"
                    dense
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.reviewed`]="{ header }">
                <div class="d-flex justify-center">
                    <v-select
                        v-model="selectReviewed"
                        :label="header.text"
                        :items="filterForCheck"
                    />
                </div>
            </template>
            <template v-slot:[`header.scheduled`]="{ header }">
                <div class="d-flex justify-center">
                    <v-select
                        v-model="selectScheduled"
                        :label="header.text"
                        :items="filterForCheck"
                    />
                </div>
            </template>
            <template v-slot:[`header.classification`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="classificationToFilter"
                    dense
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.createdBy`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.createdBy.name }}</p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.project?.reference }} - {{ item.project?.name }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.approvedDateFormatted`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{
                            item.approvedDate
                                ? formatDate(item.approvedDateFormatted, 'L')
                                : ''
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.productionStartFormatted`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{
                            item.productionStart
                                ? formatDate(item.productionStartFormatted)
                                : ''
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.processes`]="{ item }">
                <div v-for="process in item.processesInfo" :key="process.id">
                    <v-tooltip right>
                        <template v-slot:activator="{ on }">
                            <p class="my-0 text-left" v-on="on">
                                {{ process.process.name }}:
                                {{ +process.produced || 0 }}/{{
                                    +process.qty
                                }}
                                ({{
                                    (
                                        ((+process.produced || 0) * 100) /
                                        +process.qty
                                    ).toFixed(0) || 0
                                }}%)
                            </p>
                        </template>
                        <span>
                            {{ process.process.name }}: <br />
                            Total Quantity =
                            {{ +process.qty || 0 }}<br />
                            Available = {{ process.available || 0 }} ({{
                                (
                                    ((+process.available || 0) * 100) /
                                    +process.qty
                                ).toFixed(0) || 0
                            }}%)<br />
                            Produced =
                            {{ +process.produced || 0 }} ({{
                                (
                                    ((+process.produced || 0) * 100) /
                                    +process.qty
                                ).toFixed(0) || 0
                            }}%)<br />
                            Delivered =
                            {{ +process.delivered || 0 }} ({{
                                (
                                    ((+process.delivered || 0) * 100) /
                                    +process.qty
                                ).toFixed(0) || 0
                            }}%)
                        </span>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:[`item.quality`]="{ item }">
                <div>
                    <v-tooltip right>
                        <template v-slot:activator="{ on }">
                            <v-col>
                                <v-btn
                                    x-small
                                    icon
                                    class="mr-1"
                                    color="white"
                                    :style="{ 'background-color': '#2b81d6' }"
                                    v-on="on"
                                >
                                    <p class="my-0">
                                        {{ item.qualitySummary?.pending || 0 }}
                                    </p>
                                </v-btn>
                                <v-btn
                                    x-small
                                    icon
                                    class="mr-1"
                                    color="white"
                                    :style="{ 'background-color': '#2fbf56' }"
                                    v-on="on"
                                >
                                    <p class="my-0">
                                        {{ item.qualitySummary?.released || 0 }}
                                    </p>
                                </v-btn>
                                <v-btn
                                    x-small
                                    icon
                                    class="mr-1"
                                    color="white"
                                    :style="{ 'background-color': '#ffa41d' }"
                                    v-on="on"
                                >
                                    <p class="my-0">
                                        {{
                                            item.qualitySummary?.reprocess || 0
                                        }}
                                    </p>
                                </v-btn>
                                <v-btn
                                    x-small
                                    icon
                                    class="mr-1"
                                    color="white"
                                    :style="{ 'background-color': '#FF2610' }"
                                    v-on="on"
                                >
                                    <p class="my-0">
                                        {{ item.qualitySummary?.rejected || 0 }}
                                    </p>
                                </v-btn>
                            </v-col>
                        </template>
                        <span>
                            Pending :
                            {{ item.qualitySummary?.pending || 0 }}
                            <br />
                            Released :
                            {{ item.qualitySummary?.released || 0 }} <br />
                            Reprocess :
                            {{ item.qualitySummary?.reprocess || 0 }} <br />
                            Rejected :
                            {{ item.qualitySummary?.rejected || 0 }} <br />
                        </span>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:[`item.percentageReceived`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.percentageReceivedText }}</p>
                </div>
            </template>
            <template v-slot:[`item.percentageSent`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.percentageSentText }}</p>
                </div>
            </template>
            <template v-slot:[`item.reviewed`]="{ item }">
                <div class="d-flex justify-center">
                    <v-simple-checkbox
                        color="primary"
                        v-model="item.reviewed"
                        @click="setItemReviewed(item)"
                    />
                </div>
            </template>
            <template v-slot:[`item.scheduled`]="{ item }">
                <div class="d-flex justify-center">
                    <v-simple-checkbox
                        color="primary"
                        v-model="item.scheduled"
                        :disabled="!item.reviewed"
                        v-on:click="
                            item.reviewed ? setItemScheduled(item) : null
                        "
                    />
                </div>
            </template>
            <template v-slot:[`item.classification`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.classification ?? null }}</p>
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <v-menu rounded offset-y>
                    <template v-slot:activator="{ attrs, on }">
                        <v-btn
                            fab
                            v-bind="attrs"
                            v-on="on"
                            small
                            color="grey lighten-3"
                            depressed
                            class="mx-1"
                        >
                            <v-icon>mdi-dots-horizontal</v-icon>
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item @click.stop="openProcessWorkOrder(item)">
                            <v-list-item-icon>
                                <v-icon size="20">mdi-pencil</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>
                                    Modify
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item
                            v-if="productionFinishPermission"
                            @click.stop="openFinishProduction(item)"
                        >
                            <v-list-item-icon>
                                <v-icon size="20">mdi-wrench-check</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>
                                    Finish Production
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </v-data-table>
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <!--PROCESS WORK ORDER-->
        <v-dialog
            v-model="processWorkOrder"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <ProcessWorkOrder
                v-if="processWorkOrder"
                :order="selectedOrder"
                @close="closeProcessWorkOrder"
            />
        </v-dialog>
        <!-- finish work order -->
        <v-dialog v-model="finishProduction" persistent max-width="400px">
            <v-card :loading="loading">
                <v-card-title class="text-h5">Finish Work Order</v-card-title>
                <v-card-text>
                    Are you sure you want to finish this work order?
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        color="secondary"
                        @click="closeFinishProduction"
                    >
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="finishWO"
                        text
                        color="primary"
                        :loading="loading"
                    >
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import firebase from 'firebase'
import { firestore } from '@/services/firebase'
import API from '@/services/api'
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex'
import WOMethods from './WOMethods.js'

export default {
    name: 'ApprovedWO',
    props: {
        projectId: {
            type: String,
            default: () => undefined,
        },
        project: {
            type: Object,
            deafult: () => ({}),
        },
    },
    components: {
        ProcessWorkOrder: () =>
            import('@/components/WorkOrders/ProcessWorkOrder.vue'),
    },
    data: () => ({
        filterForCheck: ['all', 'checked', 'unchecked'],
        selectReviewed: 'all',
        selectScheduled: 'all',
        deleteWarning: false,
        selectedOrder: undefined,
        processWorkOrder: false,
        workOrder: false,
        projectToFilter: undefined,
        projectToFilterDebounced: undefined,
        typeToFilter: undefined,
        typeToFilterDebounced: undefined,
        classificationToFilter: undefined,
        classificationToFilterDebounced: undefined,
        loading: false,
        workOrders: [],
        activateAlert: false,
        alertMessage: undefined,
        search: null,
        alertsListener: null,
        searchDebounced: null,
        user: {},
        users: {},
        projects: {},
        headers: [
            {
                text: 'ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'CREATED BY',
                value: 'createdBy',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'center',
                sortable: false,
                width: 240,
            },
            {
                text: 'APPROVED DATE',
                value: 'approvedDateFormatted',
                align: 'center',
                sortable: true,
            },
            {
                text: 'PRODUCTION START',
                value: 'productionStartFormatted',
                align: 'center',
                sortable: true,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'center',
                sortable: false,
            },
            {
                text: 'QUALITY',
                value: 'quality',
                align: 'center',
                sortable: false,
            },
        ],
        company: JSON.parse(localStorage.getItem('company')),
        listener: undefined,
        companyId: JSON.parse(localStorage.getItem('company')),
        enableOptions: true,
        resourceId: undefined,
        showProcesses: false,
        settingProcesses: undefined,
        qualityProcess: {
            id: 'quality',
            name: 'Quality',
        },
        readyForPackingProcess: {
            id: 'readyForPacking',
            name: 'Packing',
        },
        packingProcess: {
            id: 'packing',
            name: 'Packing',
        },
        productionFinishPermission: false,
        finishProduction: false,
        woMethods: new WOMethods(),
        deletedWorkOrdersIds: [],
    }),
    computed: {
        ...mapState(['notificationResource']),
        workOrdersFiltered() {
            this.openResource()
            let conditions = []
            if (this.searchDebounced) {
                conditions.push(this.filterOrder)
            }
            if (this.projectToFilterDebounced) {
                conditions.push(this.filterProject)
            }
            if (this.selectReviewed) {
                conditions.push(this.filterReviewed)
            }
            if (this.selectScheduled) {
                conditions.push(this.filterScheduled)
            }
            if (this.typeToFilterDebounced) {
                conditions.push(this.filterByType)
            }
            if (this.classificationToFilterDebounced) {
                conditions.push(this.filterByClassification)
            }
            if (conditions.length > 0) {
                return this.workOrders.filter(order => {
                    return conditions.every(condition => {
                        return condition(order)
                    })
                })
            }
            this.sort()
            return this.workOrders
        },
    },
    watch: {
        notificationResource: function(resource) {
            if (resource) {
                this.resourceId = resource
                this.openResource()
                this.setNotificationResource(undefined)
            }
        },
        search: function(newValue) {
            this.debouncedSearch(newValue)
        },
        projectToFilter: function(newValue) {
            this.debouncedProjectFilter(newValue)
        },
        typeToFilter: function(newValue) {
            this.debouncedTypeFilter(newValue)
        },
        classificationToFilter: function(newValue) {
            this.debouncedClassificationFilter(newValue)
        },
    },
    created() {
        this.debouncedSearch = _.debounce(value => {
            this.searchDebounced = value
        }, 400)
        this.debouncedProjectFilter = _.debounce(value => {
            this.projectToFilterDebounced = value
        }, 400)
        this.debouncedTypeFilter = _.debounce(value => {
            this.typeToFilterDebounced = value
        }, 400)
        this.debouncedClassificationFilter = _.debounce(value => {
            this.classificationToFilterDebounced = value
        }, 400)
    },
    async mounted() {
        try {
            this.loading = true
            this.resourceId = this.$route.query.resourceId

            const {
                user,
                projects,
                users,
                settings,
            } = await this.woMethods.bootstrapWoLookups({
                includeSettings: true,
            })
            this.user = user
            this.projects = projects
            this.users = users

            // set permission
            this.productionFinishPermission = user.permissions.includes(
                'finishWO'
            )
            // validate permission
            if (user.permissions.includes('watchProcesses')) {
                this.showProcesses = true
            }

            if (this.showProcesses) {
                this.headers.splice(6, 0, {
                    text: 'PROCESSES',
                    value: 'processes',
                    align: 'center',
                    sortable: false,
                })
            }

            if (user.process) {
                this.headers.splice(7, 0, {
                    text: '% RECEIVED',
                    value: 'percentageReceived',
                    align: 'center',
                    sortable: false,
                    width: 100,
                })
                this.headers.splice(8, 0, {
                    text: '% SENT',
                    value: 'percentageSent',
                    align: 'center',
                    sortable: false,
                    width: 100,
                })
            }

            if (user.permissions.includes('updateProductionInfo')) {
                this.headers.splice(9, 0, {
                    text: 'REVIEWED',
                    value: 'reviewed',
                    align: 'center',
                    sortable: false,
                })
                this.headers.splice(10, 0, {
                    text: 'SCHEDULED',
                    value: 'scheduled',
                    align: 'center',
                    sortable: false,
                })
            }

            this.headers.push({
                text: 'CLASSIFICATION',
                value: 'classification',
                align: 'center',
                sortable: false,
            })
            this.headers.push({
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
            })
            this.settingProcesses = settings?.find(s => s.name == 'Processes')

            await this.getWOFromSDK()
            await this.loadAlertSummaries()
            this.setupRealtimeAlertsListener()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        if (this.listener) {
            this.listener()
            this.listener = null
        }
        if (this.alertsListener) {
            this.alertsListener()
            this.alertsListener = null
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ...mapActions(['setNotificationResource']),
        ...mapGetters(['getNotificationResource']),
        async loadAllActiveWorkOrders() {
            return await this.woMethods.loadAllActiveWorkOrders(
                this.companyId,
                ['approved'],
                false,
                ['rejected', 'approved', 'finished', 'deleted']
            )
        },
        async getWOFromSDK() {
            try {
                this.loading = true

                let startDate = new Date()

                // Query with status filter only (readAllWO filtered in code)
                // Note: ApprovedWO only shows 'approved' status, not 'finished'
                let query = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('workOrders')
                    .where('status', 'in', [
                        'rejected',
                        'approved',
                        'finished',
                        'deleted',
                    ])
                    .where('updatedOn', '>=', startDate)

                // Load initial data from backup
                let initialData = await this.loadAllActiveWorkOrders()

                let data = await this.woMethods.mapInBatches(
                    initialData,
                    async wo => {
                        const formatted = await this.formatWorkOrder(wo)
                        if (formatted) {
                            this.attachListMetrics(formatted)
                        }
                        return formatted
                    }
                )

                this.workOrders = data

                this.listener = query.onSnapshot(
                    async snapshot => {
                        try {
                            const changes = Array.from(snapshot.docChanges())
                            const processedChanges = await Promise.all(
                                changes.map(async change => {
                                    try {
                                        let wo = Object.assign(
                                            change.doc.data(),
                                            {
                                                id: change.doc.id,
                                            }
                                        )
                                        wo = await this.formatWorkOrder(wo)
                                        return { change, wo }
                                    } catch (error) {
                                        console.error(
                                            'Error processing change:',
                                            error
                                        )
                                        return { change, wo: null }
                                    }
                                })
                            )

                            // Process all changes after all have been formatted
                            processedChanges.forEach(({ change, wo }) => {
                                if (wo) {
                                    this.attachListMetrics(wo)
                                    if (
                                        change.type === 'added' ||
                                        change.type === 'modified'
                                    ) {
                                        const index = data.findIndex(
                                            w => w.id == wo.id
                                        )
                                        if (index >= 0) {
                                            // Use $set to ensure Vue reactivity
                                            this.$set(data, index, wo)
                                        } else {
                                            data.push(wo)
                                        }
                                    }
                                    if (
                                        change.type === 'removed' ||
                                        wo.status !== 'approved'
                                    ) {
                                        const index = data.findIndex(
                                            w => w.id == wo.id
                                        )
                                        if (index >= 0) {
                                            data.splice(index, 1)
                                        }
                                    }
                                }
                            })
                            // Force array update to ensure reactivity
                            this.workOrders = [...data]

                            // update selectedOrder if it's currently open
                            if (this.selectedOrder && this.selectedOrder.id) {
                                const updatedOrder = this.workOrders.find(
                                    wo => wo.id == this.selectedOrder.id
                                )
                                if (updatedOrder) {
                                    this.selectedOrder = _.cloneDeep(
                                        updatedOrder
                                    )
                                }
                            }
                        } catch (error) {
                            console.error(
                                'Error in onSnapshot callback:',
                                error
                            )
                            this.setErrorItems({
                                source: this.$options.name,
                                message: error.message,
                            })
                        }
                    },
                    error => {
                        // Handle snapshot errors
                        console.error('Firestore snapshot error:', error)
                        this.setErrorItems({
                            source: this.$options.name,
                            message: error.message,
                        })
                    }
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async formatWorkOrder(wo) {
            try {
                const workOrder = wo
                workOrder.productionStartFormatted = workOrder.productionStart
                    ? workOrder.productionStart.seconds ||
                      workOrder.productionStart._seconds
                    : undefined
                workOrder.approvedDateFormatted = workOrder.approvedDate
                    ? workOrder.approvedDate.seconds ||
                      workOrder.approvedDate._seconds
                    : undefined

                // Filter by status (already in query, but double check)
                // ApprovedWO only shows 'approved' status
                if (
                    !['rejected', 'approved', 'finished', 'deleted'].includes(
                        workOrder.status
                    )
                ) {
                    return undefined
                }

                // Filter by readAllWO permission in code (not in query)
                const createdById = this.woMethods.createdById(
                    workOrder.createdBy
                )
                if (
                    !this.user?.permissions?.includes('readAllWO') &&
                    createdById != this.user.id
                ) {
                    return undefined
                }

                this.woMethods.assignCreatedBy(workOrder, this.users)

                // get or fetch project
                workOrder.project = this.projects[workOrder.projectId] || {}
                if (
                    !workOrder.project ||
                    Object.keys(workOrder.project).length == 0
                ) {
                    workOrder.project = await API.getProject(
                        workOrder.projectId
                    )
                    // add project to projects object
                    this.projects[workOrder.projectId] = workOrder.project
                }

                // Format items
                const formattedWO = this.woMethods.formatItems(workOrder)
                return formattedWO || workOrder
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
                return undefined
            }
        },
        async finishWO() {
            try {
                this.loading = true
                await API.updateWorkOrder({
                    workOrderId: this.selectedOrder.id,
                    status: 'finished',
                    notificationId: 'pFDh25EcR74EMiuypq8M',
                })
                this.finishProduction = false
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeFinishProduction() {
            this.selectedOrder = {}
            this.finishProduction = false
        },
        openFinishProduction(workOrder) {
            this.selectedOrder = _.cloneDeep(workOrder)
            this.finishProduction = true
        },
        openProcessWorkOrder(item) {
            this.openProcessWorkOrderAsync(item)
        },
        async openProcessWorkOrderAsync(item) {
            try {
                this.loading = true
                let order = await this.woMethods.hydrateWorkOrderOnOpen(
                    this.companyId,
                    item
                )
                order = _.cloneDeep(order)
                order = (await this.formatWorkOrder(order)) || order
                this.attachListMetrics(order)
                this.selectedOrder = _.cloneDeep(order)
                this.processWorkOrder = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeProcessWorkOrder() {
            this.processWorkOrder = false
        },
        filterOrder(order) {
            return order.code
                .toLowerCase()
                .includes(this.searchDebounced.toLowerCase())
        },
        filterProject(order) {
            return (
                order.project?.name
                    .toLowerCase()
                    .includes(this.projectToFilterDebounced.toLowerCase()) ||
                order.project?.reference
                    .toString()
                    .toLowerCase()
                    .includes(this.projectToFilterDebounced.toLowerCase())
            )
        },
        filterByType(order) {
            const type = order.assemblyWorkOrder ? 'assembly' : 'production'
            return type.includes(this.typeToFilterDebounced.toLowerCase())
        },
        filterByClassification(order) {
            const classification = (order.classification ?? '').toString()
            return classification
                .toLowerCase()
                .includes(this.classificationToFilterDebounced.toLowerCase())
        },
        filterReviewed(order) {
            if (this.selectReviewed == 'all') {
                return true
            } else if (this.selectReviewed == 'checked' && order.reviewed) {
                return true
            } else if (this.selectReviewed == 'unchecked' && !order.reviewed) {
                return true
            } else return false
        },
        filterScheduled(order) {
            if (this.selectScheduled == 'all') {
                return true
            } else if (this.selectScheduled == 'checked' && order.scheduled) {
                return true
            } else if (
                this.selectScheduled == 'unchecked' &&
                !order.scheduled
            ) {
                return true
            } else return false
        },
        formatDate(seconds) {
            return `${moment.unix(seconds).format('L')} ${moment
                .unix(seconds)
                .format('LT')}`
        },
        async exportApprovedWOReport() {
            if (!this.settingProcesses || !this.settingProcesses.processes) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'Process settings not loaded.',
                })
                return
            }
            const rows = this.workOrdersFiltered.map(item => {
                const approvedDateStr = item.approvedDate
                    ? this.formatDate(
                          item.approvedDateFormatted ||
                              item.approvedDate.seconds ||
                              item.approvedDate._seconds
                      )
                    : ''
                const productionStartStr = item.productionStart
                    ? this.formatDate(
                          item.productionStartFormatted ||
                              item.productionStart.seconds ||
                              item.productionStart._seconds
                      )
                    : ''
                const qualityStr = `Pending: ${item.qualitySummary?.pending ??
                    this.calculateWOPending(item)}, Released: ${item
                    .qualitySummary?.released ??
                    this.calculateWOReleased(item)}, Reprocess: ${item
                    .qualitySummary?.reprocess ??
                    this.calculateWOReprocess(item)}, Rejected: ${item
                    .qualitySummary?.rejected ??
                    this.calculateWORejected(item)}`
                const projectStr = item.project
                    ? `${item.project.reference || ''} - ${item.project.name ||
                          ''}`.trim()
                    : ''
                const row = {
                    ORDER: item.code,
                    'CREATED BY': item.createdBy?.name || '',
                    PROJECT: projectStr,
                    'APPROVED DATE': approvedDateStr,
                    'PRODUCTION START': productionStartStr,
                    NOTES: item.notes || '',
                }
                if (this.showProcesses && this.settingProcesses.processes) {
                    const processList = [
                        ...(this.settingProcesses.processes || []),
                    ]
                    if (
                        !processList.some(p => p.id === this.qualityProcess.id)
                    ) {
                        processList.push(this.qualityProcess)
                    }
                    if (
                        !processList.some(p => p.id === this.packingProcess.id)
                    ) {
                        processList.push(this.packingProcess)
                    }
                    processList.forEach(process => {
                        const info = (item.processesInfo || []).find(
                            pi => pi.process && pi.process.id === process.id
                        )
                        if (info && info.qty) {
                            const produced = +info.produced || 0
                            const qty = +info.qty
                            const pct = ((produced * 100) / qty).toFixed(0)
                            row[process.name] = `${produced}/${qty} (${pct}%)`
                        } else {
                            row[process.name] = ''
                        }
                    })
                }
                if (this.user.process) {
                    row['% RECEIVED'] =
                        item.percentageReceivedText ||
                        this.getPercentageReceived(item)
                    row['% SENT'] =
                        item.percentageSentText || this.getPercentageSent(item)
                }
                if (
                    this.user.permissions &&
                    this.user.permissions.includes('updateProductionInfo')
                ) {
                    row.REVIEWED = item.reviewed ? 'Yes' : 'No'
                    row.SCHEDULED = item.scheduled ? 'Yes' : 'No'
                }
                row.QUALITY = qualityStr
                row.CLASSIFICATION =
                    item.classification != null ? item.classification : ''
                return row
            })
            if (rows.length === 0) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'There is no data to export.',
                })
                return
            }
            const xlsxModule = await import('xlsx/xlsx.mjs')
            const XLSX = xlsxModule.default || xlsxModule
            const ws = XLSX.utils.json_to_sheet(rows)
            const formattedDate = moment().format('YYYY-MM-DD_HH-mm')
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'ApprovedWorkOrders')
            XLSX.writeFile(wb, `approved_work_orders_${formattedDate}.xlsx`)
        },
        sort() {
            this.workOrders.sort(
                (a, b) =>
                    (b.createdOn.seconds || b.createdOn._seconds) -
                    (a.createdOn.seconds || a.createdOn._seconds)
            )
        },

        async setItemReviewed(item) {
            await this.updateProductionInfo(item, 'reviewed')
        },

        async setItemScheduled(item) {
            await this.updateProductionInfo(item, 'scheduled')
        },

        async updateProductionInfo(item, flagType) {
            try {
                this.loading = true
                const updateParams = {
                    workOrderId: item.id,
                }
                updateParams[flagType] = item[flagType]
                await API.updateProductionInfo(updateParams)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                if (item[flagType]) {
                    this.alertMessage = `Work Order was flagged as ${flagType}`
                    this.activateAlert = true
                }
                this.loading = false
            }
        },
        openResource() {
            if (this.resourceId) {
                const resource = this.workOrders.find(
                    w => w.id == this.resourceId
                )
                if (resource) {
                    this.resourceId = undefined
                    this.openProcessWorkOrder(resource)
                }
            }
        },
        setProcessesInfo(wo) {
            wo.processesInfo = []
            let qualityProcess = {
                process: {
                    id: this.qualityProcess.id,
                    name: this.qualityProcess.name,
                },
                qty: 0,
                received: 0,
                delivered: 0,
                produced: 0,
                available: 0,
            }

            let packingProcess = {
                process: {
                    id: this.packingProcess.id,
                    name: this.packingProcess.name,
                },
                qty: 0,
                received: 0,
                delivered: 0,
                produced: 0,
                available: 0,
            }

            const items = wo.items || []
            items.forEach(item => {
                if (item.processes) {
                    item.processes?.forEach(process => {
                        let processInfo = wo.processesInfo.find(
                            info => info.process.id == process
                        )

                        if (!processInfo) {
                            let totalDelivered = item.deliveries
                                ? item.deliveries.reduce(
                                      (total, register) =>
                                          register.prevProcess.id == process
                                              ? total + register.qty
                                              : total,
                                      0
                                  )
                                : 0
                            let totalProduced = item.production
                                ? item.production.reduce(
                                      (total, register) =>
                                          register.process.id == process
                                              ? total + register.qty
                                              : total,
                                      0
                                  )
                                : 0

                            let received = item.deliveries
                                ? item.deliveries.filter(
                                      register =>
                                          register.nextProcess.id == process &&
                                          register.receivedBy
                                  )
                                : []
                            let totalReceived = received.reduce(
                                (total, register) => total + register.qty,
                                0
                            )
                            let processIndex = this.settingProcesses.processes.findIndex(
                                p => p.id == process
                            )

                            const pIndex = item.processes.findIndex(
                                p => p == process
                            )
                            if (pIndex == 0) {
                                totalReceived = item.quantity
                            }

                            if (
                                this.settingProcesses.processes[processIndex] !=
                                undefined
                            ) {
                                wo.processesInfo.push({
                                    process: {
                                        id: this.settingProcesses.processes[
                                            processIndex
                                        ].id,
                                        name: this.settingProcesses.processes[
                                            processIndex
                                        ].name,
                                    },
                                    qty: item.quantity,
                                    delivered: totalDelivered,
                                    produced: totalProduced,
                                    received: totalReceived,
                                    available: totalReceived - totalProduced,
                                })
                            }
                        } else {
                            processInfo.qty += item.quantity
                            processInfo.delivered += item.deliveries
                                ? item.deliveries.reduce(
                                      (total, register) =>
                                          register.prevProcess.id == process
                                              ? total + register.qty
                                              : total,
                                      0
                                  )
                                : 0
                            processInfo.produced += item.production
                                ? item.production.reduce(
                                      (total, register) =>
                                          register.process.id == process
                                              ? total + register.qty
                                              : total,
                                      0
                                  )
                                : 0

                            const receivedEntries = item.deliveries
                                ? item.deliveries.filter(
                                      e => e.nextProcess == process
                                  )
                                : []

                            processInfo.received += receivedEntries.reduce(
                                (total, register) => total + register.qty,
                                0
                            )
                        }
                    })

                    //packing process
                    let totalReceivedNew = item.deliveries
                        ? item.deliveries.reduce(
                              (total, register) =>
                                  register.nextProcess.id ==
                                      this.packingProcess.id &&
                                  register.receivedBy
                                      ? total + register.qty
                                      : total,
                              0
                          )
                        : 0

                    let totalReceivedOld = item.deliveries
                        ? item.deliveries.reduce(
                              (total, register) =>
                                  register.nextProcess.id ==
                                      this.readyForPackingProcess.id &&
                                  register.receivedBy
                                      ? total + register.qty
                                      : total,
                              0
                          )
                        : 0

                    let totalReceived = totalReceivedOld + totalReceivedNew

                    if (item.processes?.includes(this.packingProcess.id)) {
                        packingProcess.qty += item.quantity
                        packingProcess.delivered = 'NA'
                        packingProcess.produced += item.packedQty
                        packingProcess.available =
                            totalReceived - (packingProcess.produced || 0)
                    }

                    // quality process
                    if (item.processes?.includes(this.qualityProcess.id)) {
                        qualityProcess.qty += item.quantity
                        qualityProcess.delivered = 'NA'
                        qualityProcess.produced =
                            this.calculateRejectedQty(item) +
                            this.calculateReleasedQty(item)
                        qualityProcess.available = this.calculateQualityPending(
                            item,
                            wo
                        )
                    }
                }
            })
            if (qualityProcess.qty > 0) {
                wo.processesInfo.push(qualityProcess)
            }
            if (packingProcess.qty > 0) {
                wo.processesInfo.push(packingProcess)
            }
            if (this.selectedOrder && this.selectedOrder.id == wo.id) {
                this.selectedOrder = _.cloneDeep(wo)
            }
        },
        getPercentageReceived(wo) {
            let totalReceived = 0
            let totalItems = 0
            wo.items?.forEach(item => {
                const processIndex = item.processes?.findIndex(
                    processId => this.user.process.id == processId
                )
                if (processIndex > -1) {
                    totalItems += item.quantity
                    if (processIndex == 0) {
                        totalReceived += Number(item.quantity)
                    } else if (processIndex > 0 && item.deliveries) {
                        for (let entry of item.deliveries) {
                            if (
                                entry.nextProcess.id == this.user.process.id &&
                                entry.receivedBy
                            ) {
                                totalReceived += Number(entry.qty)
                            }
                        }
                    }
                }
            })
            const percentage = ((totalReceived / totalItems) * 100).toFixed(1)
            return isNaN(percentage) ? '-' : `${percentage}%`
        },
        getPercentageSent(wo) {
            let totalSent = 0
            let totalItems = 0
            wo.items?.forEach(item => {
                const processIndex = item.processes?.findIndex(
                    processId => this.user.process.id == processId
                )
                if (processIndex > -1) {
                    totalItems += item.quantity
                    if (item.deliveries) {
                        for (let entry of item.deliveries) {
                            if (entry.prevProcess.id == this.user.process.id) {
                                totalSent += Number(entry.qty)
                            }
                        }
                    }
                }
            })
            const percentage = ((totalSent / totalItems) * 100).toFixed(1)
            return isNaN(percentage) ? '-' : `${percentage}%`
        },
        calculateQualityPending(item, workOrder) {
            try {
                let pendingQty = 0
                let lastProcessIndex = item.processes.length - 1
                if (workOrder.assemblyWorkOrder != undefined) {
                    lastProcessIndex =
                        item.processes.findIndex(
                            p => p == this.qualityProcess.id
                        ) - 1
                }
                if (lastProcessIndex > -1) {
                    const lastProductionProcess =
                        item.processes[lastProcessIndex]
                    if (item.production) {
                        const production = item.production.filter(
                            entry => entry.process.id == lastProductionProcess
                        )
                        if (production.length > 0) {
                            pendingQty = production.reduce(
                                (accumulator, entry) => accumulator + entry.qty,
                                0
                            )
                        }
                    }
                    if (item.quality) {
                        const rejected = item.quality.reduce(
                            (accumulator, entry) =>
                                accumulator + (entry.rejectQty || 0),
                            0
                        )
                        const released = item.quality.reduce(
                            (accumulator, entry) =>
                                accumulator + (entry.releaseQty || 0),
                            0
                        )
                        pendingQty -=
                            Number(released || 0) + Number(rejected || 0)
                    }
                }
                return pendingQty
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateReleasedQty(item) {
            try {
                let released = 0
                if (item.quality) {
                    const value = item.quality.reduce(
                        (accumulator, entry) =>
                            accumulator + (entry.releaseQty || 0),
                        0
                    )
                    released += Number(value || 0)
                }
                return released
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateRejectedQty(item) {
            try {
                let rejected = 0
                if (item.quality) {
                    const value = item.quality.reduce(
                        (accumulator, entry) =>
                            accumulator + (entry.rejectQty || 0),
                        0
                    )
                    rejected += Number(value || 0)
                }
                return rejected
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        attachListMetrics(wo) {
            if (!wo) {
                return wo
            }
            if (!wo.formatted) {
                this.woMethods.formatItems(wo)
            }
            if (this.showProcesses && wo.status == 'approved') {
                this.setProcessesInfo(wo)
            }
            wo.qualitySummary = {
                pending: this.calculateWOPending(wo) || 0,
                released: this.calculateWOReleased(wo) || 0,
                reprocess: this.calculateWOReprocess(wo) || 0,
                rejected: this.calculateWORejected(wo) || 0,
            }
            if (this.user.process) {
                wo.percentageReceivedText = this.getPercentageReceived(wo)
                wo.percentageSentText = this.getPercentageSent(wo)
            }
            return wo
        },
        calculateWOPending(wo) {
            try {
                let totalPending = 0
                wo.items?.forEach(item => {
                    let pendingQty = 0

                    let lastProcessIndex = item.processes.length - 1
                    if (wo.assemblyWorkOrder != undefined) {
                        lastProcessIndex =
                            item.processes.findIndex(
                                p => p == this.qualityProcess.id
                            ) - 1
                    }
                    if (lastProcessIndex > -1) {
                        const lastProductionProcess =
                            item.processes[lastProcessIndex]
                        if (item.production) {
                            const production = item.production.filter(
                                entry =>
                                    entry.process.id == lastProductionProcess
                            )
                            if (production.length > 0) {
                                pendingQty = production.reduce(
                                    (accumulator, entry) =>
                                        accumulator + entry.qty,
                                    0
                                )
                            }
                        }
                        if (item.quality) {
                            const rejected = item.quality.reduce(
                                (accumulator, entry) =>
                                    accumulator + (entry.rejectQty || 0),
                                0
                            )
                            const released = item.quality.reduce(
                                (accumulator, entry) =>
                                    accumulator + (entry.releaseQty || 0),
                                0
                            )
                            pendingQty -=
                                Number(released || 0) + Number(rejected || 0)
                        }
                    }
                    totalPending += pendingQty
                })

                return totalPending
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateWOReleased(wo) {
            try {
                let totalReleased = 0
                wo.items?.forEach(item => {
                    let released = 0
                    if (item.quality) {
                        const value = item.quality.reduce(
                            (accumulator, entry) =>
                                accumulator + (entry.releaseQty || 0),
                            0
                        )
                        released += Number(value || 0)
                    }
                    totalReleased += released
                })
                return totalReleased
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateWOReprocess(wo) {
            try {
                let totalReprocess = 0
                wo.items?.forEach(item => {
                    let reprocess = 0
                    if (item.quality) {
                        const value = item.quality.reduce(
                            (accumulator, entry) =>
                                accumulator + (entry.reprocessQty || 0),
                            0
                        )
                        reprocess += Number(value || 0)
                    }
                    totalReprocess += reprocess
                })

                return totalReprocess
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateWORejected(wo) {
            try {
                let totalRejected = 0
                wo.items?.forEach(item => {
                    let rejected = 0
                    if (item.quality) {
                        const value = item.quality.reduce(
                            (accumulator, entry) =>
                                accumulator + (entry.rejectQty || 0),
                            0
                        )
                        rejected += Number(value || 0)
                    }
                    totalRejected += rejected
                })

                return totalRejected
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        _emptyAlertSummary() {
            return {
                total: 0,
                open: 0,
                resolved: 0,
                hasAlerts: false,
                allResolved: false,
            }
        },
        _buildAlertSummary(data) {
            const summary = this._emptyAlertSummary()
            if (data?.alerts && typeof data.alerts === 'object') {
                Object.keys(data.alerts).forEach(alertId => {
                    const alert = data.alerts[alertId]
                    summary.total++
                    if (alert.status === 'resolved') {
                        summary.resolved++
                    } else {
                        summary.open++
                    }
                })
            }
            if (summary.total > 0) {
                summary.hasAlerts = true
                summary.allResolved = summary.open === 0
            }
            return summary
        },
        _applyAlertSummary(workOrderId, summary) {
            const index = this.workOrders.findIndex(wo => wo.id === workOrderId)
            if (index >= 0) {
                this.$set(this.workOrders[index], 'alertSummary', summary)
            }
        },
        async loadAlertSummaries() {
            if (this.workOrders.length === 0) return
            try {
                const workOrderIds = this.workOrders.map(wo => wo.id)
                const summaries = await API.getAlertsSummaries(workOrderIds)
                this.workOrders.forEach(wo => {
                    this.$set(
                        wo,
                        'alertSummary',
                        summaries[wo.id] || this._emptyAlertSummary()
                    )
                })
            } catch (error) {
                this.workOrders.forEach(wo => {
                    this.$set(wo, 'alertSummary', this._emptyAlertSummary())
                })
            }
        },
        setupRealtimeAlertsListener() {
            try {
                const workOrderIds = this.workOrders.map(wo => wo.id)
                if (workOrderIds.length === 0) {
                    return
                }

                const unsubscribes = []
                const alertsCollection = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('alerts')

                for (let i = 0; i < workOrderIds.length; i += 10) {
                    const chunk = workOrderIds.slice(i, i + 10)
                    const query = alertsCollection.where(
                        firebase.firestore.FieldPath.documentId(),
                        'in',
                        chunk
                    )
                    unsubscribes.push(
                        query.onSnapshot(
                            snapshot => {
                                snapshot.docChanges().forEach(change => {
                                    const workOrderId = change.doc.id
                                    const summary =
                                        change.type === 'removed'
                                            ? this._emptyAlertSummary()
                                            : this._buildAlertSummary(
                                                  change.doc.data()
                                              )
                                    this._applyAlertSummary(
                                        workOrderId,
                                        summary
                                    )
                                })
                            },
                            error => {
                                console.error(
                                    'Error in alerts listener:',
                                    error
                                )
                                this.setErrorItems({
                                    source: this.$options.name,
                                    message: error.message,
                                })
                            }
                        )
                    )
                }

                this.alertsListener = () => {
                    unsubscribes.forEach(unsubscribe => unsubscribe())
                }
            } catch (error) {
                console.error('Error setting up alerts listener:', error)
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
    },
}
</script>

<style>
.v-input .v-label {
    font-size: 12px;
}
</style>
